import { CampaignCard } from '@/components/CampaignCardVertical';
import GridCampaignCard from '@/components/layout/GridCampaignCard';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { useMaterialEditStore } from '@/screens/MaterialEdit/store';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { CalendarPlus } from 'lucide-react';
import { useState } from 'react';

interface ScheduledCampaignsProps {
  scheduledCampaigns?: {
    campaign: CampaignDto;
    isPaused?: boolean;
    startDate?: string;
    thumbnailUrl?: string;
  }[];
  onPlayCampaignNow?: (campaignUuid: string) => void;
}

export function ScheduledCampaigns({ scheduledCampaigns = [], onPlayCampaignNow }: ScheduledCampaignsProps) {
  const { setOpenModal, removeScheduledCampaign } = useMaterialEditStore();

  function CampaignCardComponent({
    scheduledCampaign,
  }: {
    scheduledCampaign: {
      campaign: CampaignDto;
      isPaused?: boolean;
      startDate?: string;
      thumbnailUrl?: string;
    };
  }) {
    const [showModalRemoveSchedule, setShowModalRemoveSchedule] = useState(false);

    return (
      <CampaignCard
        campaign={{
          ...scheduledCampaign.campaign,
          thumbnailUrl: scheduledCampaign.thumbnailUrl,
          startDate: scheduledCampaign.startDate,
        }}
        showTitle={false}
        showSetRemoveSchedule={showModalRemoveSchedule}
        actions={{
          onClick: () => setShowModalRemoveSchedule(true),
          onCloseModal: () => setShowModalRemoveSchedule(false),
          onSetRemoveSchedule: () => removeScheduledCampaign(scheduledCampaign.campaign),
          onSetPlayNow: () => onPlayCampaignNow?.(scheduledCampaign.campaign.uuid),
        }}
      />
    );
  }

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <div className="col-span-2 mt-6 mb-4 flex w-full flex-row gap-2 px-2">
        <Icon name="calendar_add_on" fill={1} size={20} className="text-primary" />
        <h3 className="text-md leading-5 font-semibold sm:text-xl">Agendada</h3>
      </div>

      {/* Carrossel horizontal para mobile */}
      {scheduledCampaigns.length > 0 ? (
        <>
          <div className="w-full pl-2 md:hidden">
            <div className="scrollbar-hide flex min-h-32 w-full overflow-x-auto">
              <div className="flex gap-2 py-2">
                {scheduledCampaigns
                  .sort((a, b) => new Date(a.startDate ?? '').getTime() - new Date(b.startDate ?? '').getTime())
                  .map((scheduledCampaign) => (
                    <div key={scheduledCampaign.campaign.uuid} className="w-[120px] flex-shrink-0">
                      <CampaignCardComponent
                        key={scheduledCampaign.campaign.uuid}
                        scheduledCampaign={scheduledCampaign}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="hidden w-full md:block">
            <GridCampaignCard>
              {scheduledCampaigns
                .sort((a, b) => new Date(a.startDate ?? '').getTime() - new Date(b.startDate ?? '').getTime())
                .map((scheduledCampaign) => (
                  <CampaignCardComponent key={scheduledCampaign.campaign.uuid} scheduledCampaign={scheduledCampaign} />
                ))}
            </GridCampaignCard>
          </div>
        </>
      ) : (
        <div className="gray-500 flex min-h-32 w-full items-start justify-start">
          <div className="flex gap-2 p-2">
            <GridCampaignCard>
              <div className="flex aspect-[9/16] h-full w-full flex-1 rounded-sm bg-gray-500/20"></div>
            </GridCampaignCard>
          </div>
        </div>
      )}
      <RestrictedComponent requiredScopes={['material:campaign']} behavior="hide">
        <div className="mt-6 flex w-full items-start justify-start">
          <Button
            variant="default"
            size="lg"
            type="button"
            onClick={() =>
              setOpenModal({
                type: 'scheduled',
                title: 'Campanhas disponíveis',
                icon: 'hub',
              })
            }
            className="w-full max-w-[400px] rounded-sm text-white sm:text-sm"
          >
            <CalendarPlus size={16} className="mr-2" />
            <span>Agendar nova campanha</span>
          </Button>
        </div>
      </RestrictedComponent>
    </div>
  );
}
